import React, { JSX, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import CatalogSection from "../components/Catalog/CatalogSection";
import SubnavLinks from "../components/layout/SubnavLinks";
import { trackSearch } from "../lib/analytics";
import { useCatalogFilters } from "../hooks/useCatalogFilters";
import { fetchShirtsFromApi, fetchShirtById } from "../services/shirtService";
import { auctionService, Auction } from "../services/auctionService";
import { ApiShirt } from "../types/Shirt";
import { hasActiveAuction } from "../utils/auctionVisibility";

export default function CatalogPage(): JSX.Element {
  const [searchParams] = useSearchParams();
  const listingType = searchParams.get("type") ?? "";
  const searchQuery = (searchParams.get("q") ?? "").trim().toLowerCase();

  const isAuction = listingType === "auction" || listingType === "ending-soon";
  const showTopGrid = !listingType || listingType === "discover";

  const [apiShirts, setApiShirts] = useState<ApiShirt[]>([]);
  const [auctions, setAuctions] = useState<Auction[]>([]);

  useEffect(() => {
    let shirtsResult: ApiShirt[] = [];

    fetchShirtsFromApi()
      .then((shirts) => {
        shirtsResult = shirts;
        setApiShirts(shirts);
        return auctionService.listAuctions();
      })
      .then(async (auctionList) => {
        setAuctions(auctionList);
        const knownIds = new Set(shirtsResult.map((s) => s.id));
        const missing = auctionList.filter((a) => !knownIds.has(a.shirt_id));
        if (missing.length > 0) {
          const extra = await Promise.all(
            missing.map((a) => fetchShirtById(a.shirt_id).catch(() => null)),
          );
          const valid = extra.filter((s): s is ApiShirt => s !== null);
          if (valid.length > 0) {
            // Dedupe op id: meerdere veilingen kunnen naar hetzelfde shirt
            // verwijzen (bv. herlancering), wat anders dubbele kaarten geeft.
            setApiShirts((prev) => {
              const byId = new Map(prev.map((s) => [s.id, s]));
              for (const s of valid) if (!byId.has(s.id)) byId.set(s.id, s);
              return Array.from(byId.values());
            });
          }
        }
      })
      .catch((err) => console.error("Failed to load data:", err));
  }, []);

  const auctionByShirtId = useMemo(() => {
    const map = new Map<string, Auction>();
    const nowMs = Date.now();
    for (const a of auctions) {
      const parsed = Date.parse(a.end_time);
      if (Number.isFinite(parsed) && parsed > nowMs) {
        map.set(a.shirt_id, a);
      }
    }
    return map;
  }, [auctions]);

  const [visible, setVisible] = useState<number>(10);
  const step = 10;

  useEffect(() => {
    setVisible(10);
  }, [listingType, searchQuery]);

  useEffect(() => {
    if (visible > 10) {
      document.getElementById("shirtsGrid")?.focus({ preventScroll: false });
    }
  }, [visible]);

  const [selectedLeagues, setSelectedLeagues] = useState<Set<string>>(
    new Set(),
  );

  const toggleLeague = (id: string) => {
    setSelectedLeagues((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const leagueOptions = [
    { id: "premier", label: "Premier League", color: "#3D1152", abbr: "PL" },
    { id: "pro", label: "Pro League", color: "#0A1628", abbr: "Pro" },
    { id: "ligue1", label: "Ligue 1", color: "#091C3E", abbr: "L1" },
    { id: "eredivisie", label: "Eredivisie", color: "#E30613", abbr: "Ere" },
    { id: "laliga", label: "LaLiga", color: "#EF3340", abbr: "LL" },
    { id: "seriea", label: "Serie A", color: "#024494", abbr: "SA" },
    { id: "bundesliga", label: "Bundesliga", color: "#D3010C", abbr: "BL" },
    { id: "rest", label: "Rest of World", color: "#2D2D2D", abbr: "ROW" },
    { id: "international", label: "World Cup", color: "#C9A84C", abbr: "WC" },
  ];

  const [selectedSeason, setSelectedSeason] = useState<string>("");

  const seasonOptions = useMemo(() => {
    const startYear = 1981;
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    const currentSeasonStart = m >= 6 ? y : y - 1;

    const seasons: string[] = [];
    for (let sy = startYear; sy <= currentSeasonStart; sy++) {
      seasons.push(`${sy}-${sy + 1}`);
    }
    return seasons;
  }, []);

  const auctionShirts = useMemo(
    () => apiShirts.filter((s) => {
      // Only show if has active auction
      if (!hasActiveAuction(s, auctionByShirtId.get(s.id))) return false;
      // Show auction shirts even if is_for_sale=false, because they're still in an active auction
      return true;
    }),
    [apiShirts, auctionByShirtId],
  );
  const fixedShirts = useMemo(
    () =>
      apiShirts.filter(
        (s) => s.sales_type !== "auction" && !auctionByShirtId.has(s.id) && s.is_for_sale !== false,
      ),
    [apiShirts, auctionByShirtId],
  );

  // When there's an active search query, search across ALL shirts (auction + fixed).
  // Otherwise respect the tab filter (auction vs fixed).
  // IMPORTANT: Filter out sold shirts (is_for_sale === false) except those in active auctions
  const mainShirts = useMemo(() => {
    if (searchQuery) {
      return apiShirts.filter((s) => {
        // Allow if in active auction
        if (hasActiveAuction(s, auctionByShirtId.get(s.id))) return true;
        // Otherwise, only show if NOT sold
        return s.is_for_sale !== false;
      });
    }
    return isAuction ? auctionShirts : fixedShirts;
  }, [searchQuery, isAuction, apiShirts, auctionShirts, fixedShirts, auctionByShirtId]);

  const matchesSearch = React.useCallback(
    (shirt: ApiShirt) => {
      if (!searchQuery) return true;
      const haystack = [
        shirt.club,
        shirt.player,
        shirt.season,
        shirt.size,
        shirt.condition,
        shirt.league,
        shirt.competition,
        shirt.description,
        shirt.seller_id,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(searchQuery);
    },
    [searchQuery],
  );

  const searchedAuctionShirts = useMemo(
    () => auctionShirts.filter(matchesSearch),
    [auctionShirts, matchesSearch],
  );

  const searchedMainShirts = useMemo(
    () => mainShirts.filter(matchesSearch),
    [mainShirts, matchesSearch],
  );

  // search-event: één push per zoekterm (searchQuery komt uit de URL, dus
  // verandert enkel bij een echte zoekactie).
  useEffect(() => {
    if (!searchQuery) return;
    trackSearch(
      searchQuery,
      searchedAuctionShirts.length + searchedMainShirts.length,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const {
    selectedSizes,
    priceMin,
    priceMax,
    sortBy,
    filteredItems,
    toggleSize,
    setPriceMin,
    setPriceMax,
    setSortBy,
    clearFilters,
  } = useCatalogFilters(searchedMainShirts, selectedLeagues, selectedSeason, auctionByShirtId, listingType);

  const clearAll = () => {
    clearFilters();
    setSelectedSeason("");
    setSelectedLeagues(new Set());
  };

  // Zodra er een filter actief is verbergen we de ongefilterde
  // "aanbevolen"-rij, anders lijken de filters geen effect te hebben.
  const filtersActive =
    selectedLeagues.size > 0 ||
    !!selectedSeason ||
    selectedSizes.size > 0 ||
    priceMin !== "" ||
    priceMax !== "";

  return (
    <>
      <a className="skip-link" href="#main">
        Direct naar inhoud
      </a>

      <main id="main" className="site-main">
        <SubnavLinks />

        <CatalogSection
          filterProps={{
            resultsCount: filteredItems.length,
            leagueOptions,
            selectedLeagues,
            onToggleLeague: toggleLeague,
            seasonOptions,
            selectedSeason,
            onSeason: setSelectedSeason,
            selectedSizes,
            priceMin,
            priceMax,
            sortBy,
            onToggleSize: toggleSize,
            onPriceMin: setPriceMin,
            onPriceMax: setPriceMax,
            onSortBy: setSortBy,
            onClear: clearAll,
          }}
          gridPropsTop={
            showTopGrid && !searchQuery && !filtersActive
              ? {
                  apiShirts: searchedAuctionShirts.slice(0, 4),
                  auctionMap: auctionByShirtId,
                  visible: 4,
                  canLoadMore: false,
                  label: "Shirts (aanbevolen)",
                  gridId: "shirtsGridTop",
                }
              : undefined
          }
          gridPropsMain={{
            apiShirts: filteredItems,
            auctionMap: auctionByShirtId,
            visible,
            canLoadMore: visible < filteredItems.length,
            onLoadMore: () =>
              setVisible((v) => Math.min(v + step, filteredItems.length)),
            label: "Shirts (meer)",
            gridId: "shirtsGrid",
            isAuction,
            onClearFilters: clearAll,
          }}
        />
      </main>
    </>
  );
}
